/**
 * EventBus — global pub/sub.
 *
 * Systems talk to each other ONLY through the bus. No direct cross-system
 * references. This keeps the code modular enough that adding a new stage,
 * mutation family, or UI panel never requires editing unrelated systems.
 *
 * To add a new event:
 *   1. Add its name + payload type to the `EventMap` interface below.
 *   2. `bus.emit('your:event', payload)` from one place.
 *   3. `bus.on('your:event', handler)` from any number of places.
 *
 * Keep event names lowercase and namespaced with `:` (category:action).
 */

export interface EventMap {
  // food / absorption
  'food:absorbed': { foodId: string; value: number; dna: number; x: number; y: number; bloomId?: number };
  'food:spawned': { foodId: string; entity: number };
  'bloom:cleared': { bloomId: number; x: number; y: number; shape: string; tier: 'main' | 'satellite' };

  // player
  'player:damaged': { amount: number; by: string };
  'player:died': void;
  'player:moved': { x: number; y: number };
  'player:pulse': { x: number; y: number; radius: number };
  'player:dashed': { x: number; y: number; dx: number; dy: number };

  // combo
  'combo:changed': { combo: number; multiplier: number };
  'combo:milestone': { combo: number };
  'combo:reset': void;
  /** Same-type food streak (phase 1 only — fires while the absorption
   *  halo is off and the player is touch-eating one type in a row).
   *  foodId=null means the streak just reset. */
  'combo:streak': { foodId: string | null; count: number; multiplier: number };
  /** Fires each time the same-type streak crosses a multiple of 10
   *  (10, 20, 30, ...). Stages decide what to drop in response. */
  'combo:streakReward': { count: number; foodId: string };

  // upgrades / mutations
  'upgrade:bought': { upgradeId: string; level: number };
  'mutation:picked': { mutationId: string };
  'mutation:ranked': { mutationId: string; rank: number };
  'mutation:synergy': { family: string; active: boolean };
  'mutation:slotUnlocked': { slot: number };

  // environment / events
  'event:warning': { eventId: string; secondsUntil: number };
  'event:started': { eventId: string };
  'event:ended': { eventId: string };

  // codex
  'codex:unlocked': { entryId: string };

  // predators
  /** Fires once when a hostile entity is destroyed by the player. */
  'predator:killed': { predatorId: string; x: number; y: number };

  // achievements
  /** Granted on first time an achievement's trigger condition is satisfied. */
  'achievement:earned': { achievementId: string; reward: number; title: string };
  /** Genome Shard balance changed (achievement reward or heritage purchase). */
  'meta:shardsChanged': { shards: number };

  // stage
  /** A stage finished init and is running (every level, incl. restarts). */
  'stage:started': { stageId: string };
  'stage:complete': { stageId: string; summary: Record<string, unknown> };
  'stage:tick': { dt: number; elapsed: number };
  'stage:goalBanner': { title: string; subtitle: string; durationSec: number };
  'stage:advance': void;

  // scoreboard
  /** A scored stage's biomass goal was just reached. */
  'score:goalReached': { stageId: string };
  /** Scoreboard result after reaching a goal. rank is null unless status is 'ok'. */
  'score:submitted': { stageId: string; score: number; rank: number | null; status: 'ok' | 'unranked' | 'offline' };

  // currencies
  'biomass:changed': { value: number; delta: number };
  'dna:changed': { value: number; delta: number };
  'atp:changed': { value: number; max: number };
  'hp:changed': { value: number; max: number };

  // fx
  'fx:screenShake': { intensity: number; duration: number };
  'fx:timeSlow': { factor: number; duration: number };
  'fx:flash': { color: number; alpha: number; duration: number };
  'fx:floatingText': { x: number; y: number; text: string; color: number };
  'fx:particles': { x: number; y: number; count: number; color: number; spread?: number };

  // audio
  'audio:pop': { pitch: number; volume?: number };
  'audio:cue': { id: string; volume?: number };
  'audio:setVolume': { value: number };
  'audio:setMuted': { muted: boolean };
  'music:setTrack': { id: 'main' | 'multicell' | 'cambrian' };

  // save
  'save:requested': void;
  'save:loaded': void;

  // pause
  'game:paused': void;
  'game:resumed': void;

  // powerups
  'powerup:bombDetonated': { x: number; y: number; killed: number };
  'powerup:shieldActivated': { duration: number };
  'powerup:spawned': { kind: 'bomb' | 'shield'; x: number; y: number };

  // dev cheats
  'cheat:autoBuyUpgrades': void;

  // --- cambrian (stage 3) ---
  /** Ecosystem tier population changed. */
  'cambrian:ecosystem:changed': { populations: Record<number, number>; caps: Record<number, number> };
  /** A creature ate another (player or NPC). */
  'cambrian:kill': { killerId: number; victimId: number; victimTier: number; victimDefId: string; byPlayer: boolean; x: number; y: number; biomass: number; xp: number };
  /** Player reached a new evolution this run (count = evolutions so far). */
  'cambrian:evolved': { count: number };
  /** A body part was grown/upgraded; `maxed` = parts now at max level. */
  'cambrian:partGrown': { partId: string; level: number; maxed: number };
  /** Frenzy kill chain grew to `count`. */
  'cambrian:frenzy': { count: number };
  /** A whole plankton line was eaten in order. */
  'cambrian:perfectLine': void;
  /** Apex predator spawned. */
  'cambrian:apex:spawned': { entityId: number };
  /** Apex predator killed (by player). */
  'cambrian:apex:killed': void;
  /** Run ended (death / timer / apex kill — payload tells which). */
  'cambrian:run:ended': { reason: 'death' | 'timer' | 'apex_killed' | 'apex_evaded'; elapsed: number };
}

type EventKey = keyof EventMap;
type Handler<K extends EventKey> = (payload: EventMap[K]) => void;

export class EventBus {
  private handlers = new Map<EventKey, Set<Handler<any>>>();

  on<K extends EventKey>(event: K, handler: Handler<K>): () => void {
    let set = this.handlers.get(event);
    if (!set) { set = new Set(); this.handlers.set(event, set); }
    set.add(handler);
    return () => this.off(event, handler);
  }

  once<K extends EventKey>(event: K, handler: Handler<K>): () => void {
    const off = this.on(event, (payload) => { off(); handler(payload); });
    return off;
  }

  off<K extends EventKey>(event: K, handler: Handler<K>): void {
    this.handlers.get(event)?.delete(handler);
  }

  emit<K extends EventKey>(event: K, payload: EventMap[K]): void {
    const set = this.handlers.get(event);
    if (!set) return;
    for (const h of set) {
      try { h(payload); }
      catch (err) { console.error(`[EventBus] handler for "${event}" threw`, err); }
    }
  }

  clear(): void { this.handlers.clear(); }
}

// Singleton is convenient for a small game like this.
export const bus = new EventBus();
